import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Work as WorkIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useTranslation } from 'react-i18next';

const CareersPage: React.FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: '',
    portfolio: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [uploadingCv, setUploadingCv] = useState(false);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];
      if (!allowedTypes.includes(file.type)) {
        setError(t('careers.cvError'));
        return;
      }
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(t('careers.cvSizeError'));
        return;
      }
      setCvFile(file);
      setError(null);
    }
  };

  const handleRemoveCv = () => {
    setCvFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (!cvFile) {
        setError(t('careers.cvRequired'));
        setIsSubmitting(false);
        return;
      }

      setUploadingCv(true);
      const storage = getStorage();
      const timestamp = Date.now();
      const cvFileName = `cvs/${timestamp}_${formData.firstName}_${formData.lastName}_${cvFile.name}`;
      const cvRef = ref(storage, cvFileName);

      await uploadBytes(cvRef, cvFile);
      const cvUrl = await getDownloadURL(cvRef);
      setUploadingCv(false);

      const functions = getFunctions();
      const sendCareerApplicationEmail = httpsCallable(functions, 'sendCareerApplicationEmail');

      await sendCareerApplicationEmail({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        experience: formData.experience,
        portfolio: formData.portfolio,
        message: formData.message,
        cvUrl: cvUrl,
        cvFileName: cvFile.name
      });

      setSubmitted(true);
    } catch (err) {
      setError(t('careers.errorSubmit'));
      setUploadingCv(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: <WorkIcon />, title: t('careers.benefit1Title'), description: t('careers.benefit1Desc') },
    { icon: <SchoolIcon />, title: t('careers.benefit2Title'), description: t('careers.benefit2Desc') },
    { icon: <PeopleIcon />, title: t('careers.benefit3Title'), description: t('careers.benefit3Desc') },
    { icon: <TrendingUpIcon />, title: t('careers.benefit4Title'), description: t('careers.benefit4Desc') },
  ];

  const requirements = [
    t('careers.req1'),
    t('careers.req2'),
    t('careers.req3'),
    t('careers.req4'),
    t('careers.req5'),
    t('careers.req6'),
  ];

  if (submitted) {
    return (
      <Box sx={{ py: 8 }}>
        <Container>
          <Box sx={{ textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h3" component="h1" gutterBottom>
              {t('careers.successTitle')}
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              {t('careers.successMessage')}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setSubmitted(false)}
              sx={{ mt: 2 }}
            >
              {t('careers.submitAnother')}
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 8 }}>
      <Container>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" component="h1" gutterBottom>
            {t('careers.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            {t('careers.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
            {t('careers.whyJoin')}
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 4
          }}>
            {benefits.map((benefit, index) => (
              <Card key={index} sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{benefit.icon}</Box>
                  <Typography variant="h6" component="h3" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 6, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: '0 0 300px' }}>
            <Paper sx={{ p: 4, height: 'fit-content' }}>
              <Typography variant="h5" component="h3" gutterBottom>
                {t('careers.requirementsTitle')}
              </Typography>
              <List>
                {requirements.map((requirement, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={requirement} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 4 }}>
              <Typography variant="h5" component="h3" gutterBottom>
                {t('careers.applyTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                {t('careers.applySubtitle')}
              </Typography>

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label={t('careers.firstName')}
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      required
                    />
                    <TextField
                      fullWidth
                      label={t('careers.lastName')}
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      required
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label={t('careers.email')}
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                    />
                    <TextField
                      fullWidth
                      label={t('careers.phone')}
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label={t('careers.position')}
                    value={formData.position}
                    onChange={(e) => handleChange('position', e.target.value)}
                    placeholder={t('careers.positionPlaceholder')}
                    required
                  />
                  <TextField
                    fullWidth
                    label={t('careers.experience')}
                    value={formData.experience}
                    onChange={(e) => handleChange('experience', e.target.value)}
                    placeholder={t('careers.experiencePlaceholder')}
                    required
                  />
                  <TextField
                    fullWidth
                    label={t('careers.portfolio')}
                    value={formData.portfolio}
                    onChange={(e) => handleChange('portfolio', e.target.value)}
                    placeholder={t('careers.portfolioPlaceholder')}
                    helperText={t('careers.portfolioHelper')}
                  />

                  {/* CV Upload */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      {t('careers.cvLabel')}
                    </Typography>
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: cvFile ? 'success.main' : 'grey.300',
                        borderRadius: 2,
                        p: 3,
                        textAlign: 'center',
                        bgcolor: cvFile ? 'success.50' : 'grey.50',
                        transition: 'all 0.3s ease',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
                      }}
                    >
                      {!cvFile ? (
                        <Box>
                          <input
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                            id="cv-upload-button"
                            type="file"
                            onChange={handleCvUpload}
                          />
                          <label htmlFor="cv-upload-button">
                            <Button
                              variant="contained"
                              component="span"
                              startIcon={<CloudUploadIcon />}
                              size="large"
                            >
                              {t('careers.cvUpload')}
                            </Button>
                          </label>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            {t('careers.cvFormats')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('careers.cvMaxSize')}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachFileIcon color="success" />
                            <Box sx={{ textAlign: 'left' }}>
                              <Typography variant="body1" fontWeight={600}>{cvFile.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(cvFile.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton onClick={handleRemoveCv} color="error" size="small">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <TextField
                    fullWidth
                    label={t('careers.motivation')}
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder={t('careers.motivationPlaceholder')}
                    required
                  />
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                    {error}
                  </Alert>
                )}

                <Box sx={{ position: 'relative', mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={!isSubmitting && !uploadingCv ? <SendIcon /> : undefined}
                    disabled={isSubmitting || uploadingCv || !cvFile}
                    fullWidth
                  >
                    {uploadingCv ? t('careers.uploading') : isSubmitting ? t('careers.sending') : t('careers.submit')}
                  </Button>
                  {(isSubmitting || uploadingCv) && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CareersPage;
